import React from 'react';
import { translate } from '../../services/translate';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../MyAppText';
import moment from 'moment';
import MyDataTable from '../MyDataTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faUser } from '@fortawesome/free-solid-svg-icons';
import { ONLINE_AGENT_TIME_MINUTES } from '../../services/central-api/dispatch';
import { TextMask } from 'react-native-masked-text';
import { UnitAndDistance, UserAndDistances } from './OccurrenceDetailsDispatchTab';
import Tooltip from '../Tooltip';

interface UnitRow extends UnitAndDistance {
    equipmentExpanded?: boolean;
}

interface UserRow extends UserAndDistances {
    expanded?: boolean;
}

interface Props {
    unit: UnitRow;
    assignments: DropdownResource[];
    occurrenceSituation?: OccurrenceSituation;
}

function OccurrenceUnitContent({ unit, assignments, occurrenceSituation }: Props) {
    const { styles, theme } = useStyles(styleSheet);

    const renderAgentInformation = (user: User, unit: Unit) => {
        const userSkills = user.Skills ? user.Skills.map(skill => skill.name) : [];
        let userAssignment;
        if (unit) {
            const unitUser = unit.UnitUsers?.find(uu => uu.id == user.id);
            if (unitUser) {
                userAssignment = assignments.find((assignment) => assignment.id == unitUser.UnitUser.assignmentId);
            }
        }

        return <View style={{ paddingLeft: 96 }}>
            <View style={styles.agentRow}>
                <MyAppText style={{ fontWeight: 'bold' }}>{translate('skills')}:</MyAppText>
                {userSkills.length ?
                    <MyAppText>{userSkills.join(',')}</MyAppText>
                    : <MyAppText>{translate('noSkills')}</MyAppText>
                }
            </View>
            <View style={styles.agentRow}>
                <MyAppText style={{ fontWeight: 'bold' }}>{translate('assignment')}:</MyAppText>
                {userAssignment ?
                    <MyAppText>{userAssignment.name}</MyAppText>
                    : <MyAppText>{translate('notDefined')}</MyAppText>
                }
            </View>
            <View style={styles.agentRow}>
                <MyAppText style={{ fontWeight: 'bold' }}>{translate('role')}:</MyAppText>
                {user.role ?
                    <MyAppText>{user.role.name}</MyAppText>
                    : <MyAppText>{translate('notDefined')}</MyAppText>
                }
            </View>
            <View style={styles.agentRow}>
                <MyAppText style={{ fontWeight: 'bold' }}>{translate('phone')}:</MyAppText>
                {user.phone ?
                    <TextMask
                        style={{ color: theme.colors.labelColor, fontFamily: 'Open Sans' }}
                        type={'cel-phone'}
                        value={user.phone} />
                    : <MyAppText>{translate('dontHave')}</MyAppText>
                }
            </View>
        </View>;
    };

    const renderEquipments = (equipments: { name: string; quantity: number; }[]) => {
        return <View style={{ paddingLeft: 80 }}>
            <MyDataTable
                dense={true}
                noTableHead={true}
                columns={[{
                    selector: row => row.quantity ? `${row.name} - ${row.quantity}` : row.name,
                    grow: 2
                }]}
                data={equipments}
            />
        </View>;
    };

    const tooltipContent = (user: UserRow) => {
        if (user.agentPosition && moment(user.agentPosition.positionUpdatedAt) > moment().subtract(ONLINE_AGENT_TIME_MINUTES, 'minutes')) {
            return translate('activeAndLocation');
        } else {
            return translate('activeAndNoLocation');
        }
    };

    function getEquipmentData(equipments: Equipment[]) {
        const itensWithoutCategory = equipments?.filter(e => !e.equipmentCategory).map(e => {
            return { name: e.name, quantity: 0 };
        }) || [];
        const itensWithCategory = equipments?.filter(e => e.equipmentCategory) || [];

        const counting = itensWithCategory.reduce((acc, el) => {
            if (!el.equipmentCategory) {
                return acc;
            }

            const categoryName = el.equipmentCategory.name;
            if (!acc[categoryName]) {
                acc[categoryName] = 0;
            }

            acc[categoryName]++;

            return acc;
        }, {} as Record<string, number>);

        const itensForTable = Object.entries(counting).map(([name, quantity]) => ({
            name,
            quantity
        }));

        return [...itensWithoutCategory, ...itensForTable];
    }

    return (
        <View style={{ paddingVertical: 10, flex: 1 }}>
            <MyDataTable
                dense={true}
                noTableHead={true}
                columns={[{
                    cell: function (row) {
                        if (occurrenceSituation == 'concluded') {
                            return (
                                <View style={styles.unitRow}>
                                    <FontAwesomeIcon
                                        icon={faUser}
                                        fontSize={16}
                                        color={theme.colors.iconColor}
                                    />
                                    <MyAppText>{row.warName || row.name} - {row.registry}</MyAppText>
                                </View>
                            );
                        }
                        return (
                            <Tooltip tooltipDirection='right' style={{ width: 300 }} tooltipContent={tooltipContent(row)}>
                                <View style={styles.unitRow}>
                                    <FontAwesomeIcon
                                        icon={faUser}
                                        fontSize={16}
                                        color={
                                            row.agentPosition &&
                                                moment(row.agentPosition.positionUpdatedAt) >
                                                moment().subtract(ONLINE_AGENT_TIME_MINUTES, 'minutes')
                                                ? 'green'
                                                : 'orange'
                                        }
                                    />
                                    <MyAppText>{row.warName || row.name} - {row.registry}</MyAppText>
                                </View>
                            </Tooltip>
                        );
                    },
                    grow: 3
                },
                {
                    cell: function (row) {
                        return (
                            <View style={styles.unitRow}>
                                <MyAppText>{row.distance != Infinity && row.distance ? row.distance : row.distance == 0 ? translate('onSite') : '' }</MyAppText>
                            </View>
                        );
                    },
                    width: '100px'
                },
                {
                    cell: function () {
                        return <View style={styles.unitRow}></View>;
                    },
                    width: '76px'
                }
                ]}
                expandableRows
                expandableRowExpanded={function (row) {
                    return row.expanded || false;
                }}
                onRowExpandToggled={function (bool, row) {
                    row.expanded = bool;
                }}
                expandableRowsComponent={function ({ data }) {
                    return renderAgentInformation(data || [], unit);
                }}
                data={(unit.users || []) as UserRow[]}
            />
            {unit.equipments?.length ? (
                <MyDataTable
                    dense={true}
                    noTableHead={true}
                    columns={[{
                        cell: function (row) {
                            return (
                                <View style={styles.unitRow}>
                                    <FontAwesomeIcon icon={faBriefcase} fontSize={16} color={theme.colors.iconColor} />
                                    <MyAppText>{row.name}</MyAppText>
                                </View>
                            );
                        }
                    }]}
                    expandableRows
                    expandableRowExpanded={function () {
                        return unit.equipmentExpanded || false;
                    }}
                    onRowExpandToggled={function (bool) {
                        unit.equipmentExpanded = bool;
                    }}
                    expandableRowsComponent={function ({ data }) {
                        return renderEquipments(getEquipmentData(data.equipments) || []);
                    }}
                    data={[{ name: translate('equipments'), equipments: unit.equipments }]}
                />
            ) : null}
        </View>
    );
}

const styleSheet = createStyleSheet(() => ({
    unitRow: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 10
    },
    agentRow: {
        flexDirection: 'row',
        columnGap: 5,
        minHeight: 32,
        alignItems: 'center'
    }
}));


export default OccurrenceUnitContent;
